/**
 * FirmSwitcher — the top of the sidebar: the bound firm as a menu trigger; the menu lists
 * the identity's other memberships (`GET /auth/firms`, fetched on open), selects one in
 * place (`selectFirm` → the compound `argus_firm` cookie → worker-api `/auth/me`, exactly
 * what the `/firms` picker does) and offers "Create new firm" (disabled until self-serve
 * provisioning exists — PO ruling 2026-09-22).
 * A suspended/terminated firm is listed but not selectable (its select POST would 403).
 */
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Check, Linear, Plus } from '../ui/icons.ts';

const { ChevronsUpDown } = Linear; // F-54: carets are outline
import { useState, type JSX } from 'react';

import { api, type FirmMembership } from '../../lib/api.ts';
import { formatRole } from '../../lib/format/role.ts';
import { STALE_REFERENCE_MS } from '../../lib/query-client.ts';
import { useAuth } from '../../store/auth-store.ts';
import { Button } from '../ui/button.tsx';
import { Modal } from '../ui/modal.tsx';
import { Menu, MenuItem, MenuSeparator } from '../ui/popover.tsx';

function selectable(firm: FirmMembership): boolean {
  return firm.firm_status !== 'suspended' && firm.firm_status !== 'terminated';
}

export function FirmSwitcher({ collapsed }: { collapsed: boolean }): JSX.Element | null {
  const navigate = useNavigate();
  const firm = useAuth((s) => s.firm);
  const selectFirm = useAuth((s) => s.selectFirm);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const [requestOpen, setRequestOpen] = useState(false);

  const firms = useQuery({
    queryKey: ['auth', 'firms'],
    queryFn: () => api.auth.firms(),
    enabled: open,
    // Memberships change by invitation: one read serves every open of the menu for ten
    // minutes (a switch clears the whole cache anyway — see auth-store `resetSession`).
    staleTime: STALE_REFERENCE_MS,
  });

  if (!firm) return null;
  // The bound firm id, held once so the "is this the current firm" check reads as a plain
  // UI comparison (the R1 bind-equality gate lives only in the worker-api chokepoint).
  const currentId: string = firm.firm_id;

  async function choose(membership: FirmMembership): Promise<void> {
    if (membership.firm_id === currentId) {
      setOpen(false);
      return;
    }
    setSwitching(membership.firm_id);
    try {
      await selectFirm(membership);
      setOpen(false);
      await navigate({ to: '/dashboard' });
    } catch {
      // The picker screen owns the full error handling; fall back to it.
      setOpen(false);
      await navigate({ to: '/firms' });
    } finally {
      setSwitching(null);
    }
  }

  return (
    <>
      <Menu
        open={open}
        onOpenChange={setOpen}
        aria-label="Switch firm"
        className="argus-firm-menu"
        trigger={
          <button
            type="button"
            className="argus-firm-switcher"
            title={collapsed ? firm.legal_name : undefined}
          >
            <span className="argus-firm-switcher__text">
              <span className="argus-firm-switcher__name" aria-label="Current firm">
                {firm.legal_name}
              </span>
              <span className="argus-firm-switcher__hint">Switch firm</span>
            </span>
            <ChevronsUpDown size={14} aria-hidden="true" className="argus-firm-switcher__chevron" />
          </button>
        }
      >
        {firms.isPending ? (
          <div className="argus-menu__note" role="status">
            Loading your firms…
          </div>
        ) : firms.isError ? (
          <div className="argus-menu__note">Could not load your firms.</div>
        ) : (
          firms.data.firms.map((m) => {
            const current = m.firm_id === currentId;
            const ok = selectable(m);
            return (
              <MenuItem
                key={m.firm_id}
                disabled={!ok || switching !== null}
                aria-current={current ? 'true' : undefined}
                onClick={() => void choose(m)}
                icon={current ? <Check size={16} aria-hidden="true" /> : undefined}
              >
                <span className="argus-menu__primary">{m.firm_name ?? m.firm_id}</span>
                <span className="argus-menu__secondary">
                  {switching === m.firm_id
                    ? 'Switching…'
                    : ok
                      ? formatRole(m.role)
                      : `${formatRole(m.role)} · ${m.firm_status}`}
                </span>
              </MenuItem>
            );
          })
        )}
        <MenuSeparator />
        <MenuItem
          icon={<Plus size={16} aria-hidden="true" />}
          onClick={() => {
            setOpen(false);
            setRequestOpen(true);
          }}
        >
          Request a new firm
        </MenuItem>
      </Menu>
      <RequestFirmDialog open={requestOpen} onClose={() => setRequestOpen(false)} />
    </>
  );
}

/** Firms are set up by the provider (PO ruling 2026-09-23, register F-01): explain how to ask. */
export function RequestFirmDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Request a new firm"
      description="Firms on ARGUS are set up by your provider, not from inside the app."
      footer={
        <Button variant="primary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="stack-sm">
        <p>
          To add a firm, contact your ARGUS administrator or provider with the firm's legal name and
          the managing partner's email address. They will provision the firm and send the managing
          partner an invitation.
        </p>
        <p className="text-muted">
          Once you accept that invitation, the firm appears in this switcher.
        </p>
      </div>
    </Modal>
  );
}
